<template>
  <v-dialog
    :value="show"
    @input="$emit('on-close')"
    max-width="800px"
    :persistent="saving"
  >
    
    <v-dialog
      v-model="mediaChooserDialog"
      max-width="700px"
      v-if="canUpdate"
    >
      <v-card color="white">
        <v-card-title>Choose or Upload Image</v-card-title>
        <v-card-text>
          <media-chooser @on-select="selectImage" />
        </v-card-text>
      </v-card>
    </v-dialog>


    <v-card color="white">

      <v-overlay
        absolute
        :value="loading || saving"
        :opacity=".1"
        color="primary"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-overlay>

      <v-card-text>
        <v-row>
          <v-col cols="6">
            <div class="headline">{{ (ingredient.id ? 'Edit Ingredient' : 'Create Ingredient') }}</div>

            <v-row class="align-center">
              <v-col cols="12">
                <v-text-field
                  v-model="ingredient.name"
                  label="Name"
                  outlined
                  hide-details
                  placeholder="Type food item name"
                  :error="showErrors && !loading && !ingredient.name"
                  :readonly="!canUpdate"
                />
              </v-col>
              <v-col cols="8">
                <v-select
                  v-model="ingredient.category_id"
                  :items="categoryItems"
                  outlined
                  label="Category"
                  placeholder="Choose A Category"
                  hide-details
                  :error="showErrors && !loading && !ingredient.category_id"
                  :readonly="!canUpdate"
                />
              </v-col>
              <v-col cols="4">
                <v-btn-toggle 
                  v-model="isBeverage" 
                  mandatory 
                  color="dflBlue"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{on}">
                      <v-btn v-on="on" :disabled="loading">
                        <v-icon>mdi-food-apple</v-icon>
                      </v-btn>
                    </template>
                    <span>Food</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{on}">
                      <v-btn v-on="on" :disabled="loading">
                        <v-icon>mdi-coffee</v-icon>
                      </v-btn>
                    </template>
                    <span>Beverage</span>
                  </v-tooltip>
                </v-btn-toggle>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="preparations"
                  :items="prepItems"
                  outlined
                  chips
                  deletable-chips
                  small-chips
                  label="Preparation"
                  placeholder="Choose how the food is prepared"
                  multiple
                  hide-details
                  :readonly="!canUpdate"
                />
              </v-col>
              <v-col cols="12">
                <v-combobox
                  v-model="tags"
                  outlined
                  chips
                  deletable-chips
                  small-chips
                  label="Additional Tags"
                  multiple
                  placeholder="Type tag & press enter"
                  persistent-hint
                  hide-details
                  :readonly="!canUpdate"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6">
            <div class="nutrition-box pa-2">
              <div class="title text-left"><b>Nutrition Facts</b></div>
              <v-divider />
              <div>
                <v-row no-gutters class="flex-nowrap my-2 align-center">
                  <v-col>Serving Size</v-col>
                  <v-col style="max-width: 75px;">
                    <v-autocomplete
                      v-model="nutrient.serving_size"
                      :items="measurements"
                      outlined
                      hide-details
                      dense
                      :error="showErrors && !loading && !nutrient.serving_size"
                      :readonly="!canUpdate"
                    ></v-autocomplete>
                  </v-col>
                  <v-col>
                    <v-autocomplete
                      v-model="nutrient.serving_size_measurement"
                      :items="units"
                      hide-details
                      outlined
                      dense
                      :error="showErrors && !loading && !nutrient.serving_size_measurement"
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right" 
                      v-model="nutrient.serving_size_grams"  
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1">
                    g
                  </v-col>
                </v-row>

                <v-divider />

                <v-row no-gutters class="flex-nowrap mt-2 mb-1 align-center">
                  <v-col><b>Calories</b></v-col>
                  <v-col style="max-width: 75px">
                    <v-text-field
                      v-model="nutrient.calories"
                      outlined
                      hide-details
                      placeholder="0"
                      class="condensed"
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    
                  </v-col>
                </v-row>
                <v-row no-gutters class="flex-nowrap mb-2 align-center">
                  <v-col class="pl-4">From Fat</v-col>
                  <v-col style="max-width: 75px">
                    <v-text-field
                      v-model="nutrient.calories_from_fat"
                      outlined
                      hide-details
                      placeholder="0"
                      class="condensed"
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    
                  </v-col>
                </v-row>

                <v-divider />
                <v-row no-gutters class="flex-nowrap my-1 align-center">
                  <v-col><b>Total Fat</b></v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.fat" 
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    g
                  </v-col>
                </v-row>
                <v-row no-gutters class="flex-nowrap mb-1 align-center">
                  <v-col class="pl-4">Saturated Fat</v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.saturated_fat" 
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    g
                  </v-col>
                </v-row>
                <v-row no-gutters class="flex-nowrap mb-1 align-center">
                  <v-col class="pl-4">Trans Fat</v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.trans_fat" 
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    g
                  </v-col>
                </v-row>

                <v-row no-gutters class="flex-nowrap mb-1 align-center">
                  <v-col><b>Cholesterol</b></v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.cholesterol" 
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    mg
                  </v-col>
                </v-row>

                <v-row no-gutters class="flex-nowrap mb-1 align-center">
                  <v-col><b>Sodium</b></v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.sodium" 
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    mg
                  </v-col>
                </v-row>

                <v-row no-gutters class="flex-nowrap mb-1 align-center">
                  <v-col><b>Total Carbohydrate</b></v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.carbohydrate" 
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    g
                  </v-col>
                </v-row>
                <v-row no-gutters class="flex-nowrap mb-1 align-center">
                  <v-col class="pl-4">Dietary Fiber</v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.dietary_fiber" 
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    g
                  </v-col>
                </v-row>
                <v-row no-gutters class="flex-nowrap mb-1 align-center">
                  <v-col class="pl-4">Total Sugars</v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.sugars" 
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    g
                  </v-col>
                </v-row>

                <v-row no-gutters class="flex-nowrap mb-1 align-center">
                  <v-col><b>Protein</b></v-col>
                  <v-col style="max-width: 50px;">
                    <v-text-field 
                      hide-details 
                      outlined 
                      class="text-right condensed" 
                      placeholder="0" 
                      v-model="nutrient.protein"  
                      :readonly="!canUpdate"
                    />
                  </v-col>
                  <v-col class="flex-grow-0 flex-shrink-1 pl-1 nutrient-label-container">
                    g
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-col>

        </v-row>

        <v-row>
          <v-col cols="6">
            <v-textarea
              v-model="ingredient.description"
              outlined
              hide-details
              label="Description / Suggestion"
              placeholder="Type additional information to display..."
              :readonly="!canUpdate"
            />
          </v-col>
          <v-col cols="6">
            <v-row no-gutters class="justify-center align-center" style="height: 100%;">
              <v-col v-if="ingredient.image_id">
                <v-img
                  :src="ingredient.image.url_path"
                  contain
                  style="max-height: 100px;"
                  class="mx-auto"
                />
              </v-col>
              <v-col class="text-center" :class="{'mt-2':ingredient.image_id}" v-if="canUpdate">
                <v-btn small v-if="ingredient.image_id" class="mr-2" @click="selectImage(null)"><v-icon small class="mr-2">mdi-delete</v-icon>Remove</v-btn>
                <v-btn small @click="mediaChooserDialog = true"><v-icon small class="mr-2">mdi-image</v-icon>{{ (ingredient.image && ingredient.image.url_path ? 'Change' : 'Add Image')}}</v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="12" class="text-center title pb-0">Portions</v-col>

              <v-col cols="6 pr-1">
                <v-card color="light-blue lighten-5">
                  <v-card-text>
                    <v-row no-gutters class="align-center flex-nowrap portion-container">
                      <v-col class="subtitle-1">
                        <v-icon class="mr-2">mdi-gender-male</v-icon> Male
                      </v-col>
                      <v-col style="max-width: 75px;">
                        <v-autocomplete
                          v-model="malePortion.serving_size"
                          :items="measurements"
                          outlined
                          hide-details
                          dense
                          :placeholder="''"
                          :error="showErrors && !loading && !malePortion.serving_size"
                          :readonly="!canUpdate"
                        ></v-autocomplete>
                      </v-col>
                      <v-col class="flex-grow-0 flex-shrink-1 pl-4">
                        {{ nutrient.serving_size_measurement }}
                      </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-4">
                      <v-col>
                        <v-select
                          v-model="malePortion.visual_name"
                          :items="['fist','half-fist','egg-yolk','palm','palm-to-first-knuckle']"
                          label="Visual"
                          placeholder="No Visual"
                          outlined
                          hide-details
                          clearable
                          :readonly="!canUpdate"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="6 pl-1">
                <v-card color="pink lighten-5">
                  <v-card-text>
                    <v-row no-gutters class="align-center flex-nowrap portion-container">
                      <v-col class="subtitle-1">
                        <v-icon class="mr-2">mdi-gender-female</v-icon> Female
                      </v-col>
                      <v-col style="max-width: 75px;">
                        <v-autocomplete
                          v-model="femalePortion.serving_size"
                          :items="measurements"
                          outlined
                          hide-details
                          dense
                          :placeholder="''"
                          :error="showErrors && !loading && !femalePortion.serving_size"
                          :readonly="!canUpdate"
                        ></v-autocomplete>
                      </v-col>
                      <v-col class="flex-grow-0 flex-shrink-1 pl-4">
                        {{ nutrient.serving_size_measurement }}
                      </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-4">
                      <v-col>
                        <v-select
                          v-model="femalePortion.visual_name"
                          :items="['fist','half-fist','egg-yolk','palm','palm-to-first-knuckle']"
                          label="Visual"
                          placeholder="No Visual"
                          outlined
                          hide-details
                          clearable
                          :readonly="!canUpdate"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('on-close')">Close</v-btn>
        <v-btn text :disabled="!saveable" @click="save" v-if="canUpdate">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import foodPrep from '@/common/foodPreparations'
import MediaChooser from '@/views/media/components/MediaChooser'

export default {
  name: 'FoodEditor',
  props: {
    'show': Boolean,
    'itemId': [String, Number]
  },
  components: {
    'media-chooser': MediaChooser
  },
  data: () => ({
    dataExpansion: 'nutrient,nutrient.plural,preparations,tags,portions',

    loading: true,
    saving: false,
    showErrors: true,

    mediaChooserDialog: false,

    ingredient: {},
    nutrient: {},
    tags: [],
    preparations: [],
    malePortion: {},
    femalePortion: {},
    isBeverage: null,

    prepItems: foodPrep
  }),
  watch: {
    'show': function(newVal) {
      if(newVal && this.itemId) {
        this.reset(false);
        this.getDataFromApi();
      } else if(newVal) {
        this.reset();
      }
    }
  },
  computed: {
    measurements: function() {
      let data = [];
      for(let i = 0; i < 100; i++) {
        let text;
        if(i != 0) data.push({text: i + '', value: i});
        
        text = (i != 0 ? i + ' ⅛' : '⅛');
        data.push({text: text, value: .125 + i});
        
        text = (i != 0 ? i + ' ¼' : '¼');
        data.push({text: text, value: .25 + i});

        text = (i != 0 ? i + ' ⅓' : '⅓');
        data.push({text: text, value: .333 + i});

        text = (i != 0 ? i + ' ⅜' : '⅜');
        data.push({text: text, value: .375 + i});

        text = (i != 0 ? i + ' ½' : '½');
        data.push({text: text, value: .5 + i});

        text = (i != 0 ? i + ' ⅝' : '⅝');
        data.push({text: text, value: .625 + i});

        text = (i != 0 ? i + ' ⅔' : '⅔');
        data.push({text: text, value: .666 + i});

        text = (i != 0 ? i + ' ¾' : '¾');
        data.push({text: text, value: .75 + i});

        text = (i != 0 ? i + ' ⅞' : '⅞');
        data.push({text: text, value: .875 + i});
      }
      return data;
    },
    units: function() {
      return [
        'cup',
        'oz',
        'tbsp',
        'tsp',
        'lb',
        'ml',
        'g',
        'kg',
        'pt',
        'item',
        'unit'
      ];
    },
    currentMeasurement: function() {
      for(let i = 0; i < this.measurements.length; i++) {
        if(this.measurements[i].value == this.nutrient.serving_size) return this.measurements[i].text;
      }
      return '';
    },
    saveable: function() {
      if( this.ingredient.name && 
          this.nutrient.serving_size && 
          this.nutrient.serving_size_measurement &&
          this.malePortion.serving_size != null &&
          this.femalePortion.serving_size != null &&
          this.ingredient.category_id
      ) {
        return true;
      } else {
        return false;
      }
    },
    categoryItems: function() {
      return [
        {
          text: 'Protein',
          value: 1
        },
        {
          text: 'Fast Carb',
          value: 2
        },
        {
          text: 'Slow Carb',
          value: 3
        },
        {
          text: 'Fat',
          value: 4
        },
        {
          text: 'Condiment',
          value: 5
        },
        {
          text: 'All You Can Eat',
          value: 6
        }
      ]
    },
    canUpdate: function() {
      return this.$store.getters.userCan('sh_ingredient_u');
    }
  },
  methods: {
    reset: function(clearLoading = true) {
      this.ingredient = {};
      this.nutrient = {};
      this.malePortion = {};
      this.femalePortion = {};
      this.preparations = [];
      this.tags = [];
      this.isBeverage = 0;
      if(clearLoading) this.loading = false;
    },
    setData: function(ingredient) {
      this.ingredient = ingredient;
      if(ingredient.nutrient.serving_size) ingredient.nutrient.serving_size = parseFloat(ingredient.nutrient.serving_size);
      this.nutrient = (ingredient.nutrient || {});
      this.preparations = (ingredient.preparations || []);
      this.tags = (ingredient.tags || []);
      this.malePortion = (ingredient.portions.male || {});
      if(ingredient.portions.male && ingredient.portions.male.serving_size) ingredient.portions.male.serving_size = parseFloat(ingredient.portions.male.serving_size);
      this.femalePortion = (ingredient.portions.female || {});
      if(ingredient.portions.female && ingredient.portions.female.serving_size) ingredient.portions.female.serving_size = parseFloat(ingredient.portions.female.serving_size);
      this.isBeverage = (ingredient.is_beverage ? 1 : 0);
    },
    getDataFromApi: function() {
      if(!this.itemId) return;
      this.loading = true;
      this.$http.get('ingredients/' + this.itemId + '?expand=' + this.dataExpansion)
        .then(response => {
          this.setData(response.data);
        })
        .catch(err => {
          //if(err.response.status == 401) return;

          console.log(err.response);
          this.$swal.fire('Error', 'There was an error loading the food data', 'error');
          this.$emit('on-close');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save: function() {
      this.saving = true;
      let update = false;
      let method = 'POST';
      let url = 'ingredients';

      if(this.ingredient.id) {
        update = true;
        method = 'PUT';
        url += '/' + this.ingredient.id;
      }

      let data = {
        name: this.ingredient.name,
        description: this.ingredient.description,
        category_id: this.ingredient.category_id,
        is_beverage: (this.isBeverage ? 1 : 0),
        image_id: this.ingredient.image_id,
        setNutrient: this.nutrient,
        setTags: this.tags,
        setPreparations: this.preparations,
        setPortions: {
          male: {
            serving_size: this.malePortion.serving_size,
            visual_name: this.malePortion.visual_name,
          },
          female: {
            serving_size: this.femalePortion.serving_size,
            visual_name: this.femalePortion.visual_name,
          }
        }
      }

      this.$http({
        method: method,
        url: url + '?expand=' + this.dataExpansion,
        data: data
      })
        .then(response => {
          this.setData(response.data);
          this.$emit('on-' + (update ? 'update' : 'new'), response.data);
        })
        .catch(err => {
          console.log(err);
          //if(err.response.status == 401) return;

          console.log(err.response);
          this.$swal.fire('Error', 'There was an error saving your data', 'error');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    selectImage: function(img) {
      this.mediaChooserDialog = false;
      this.$set(this.ingredient, 'image', img);
      if(!img) {
        this.ingredient.image_id = null;
      } else {
        this.ingredient.image_id = img.id;
      }
    }
  }
};
</script>

<style lang="scss">
  .nutrition-box {
    border: 1px solid #000;

    .row:hover {
      background-color: #F6F6F6;
    }

    .v-input input {
      max-height: 32px;
    }
    .v-text-field--filled > .v-input__control > .v-input__slot, .v-text-field--full-width > .v-input__control > .v-input__slot, .v-text-field--outlined > .v-input__control > .v-input__slot {
      min-height: 32px;
    }
    .v-text-field--outlined.v-input--dense.v-text-field--outlined > .v-input__control > .v-input__slot {
      min-height: 32px;
    }
    .v-text-field.v-text-field--enclosed:not(.v-text-field--rounded) > .v-input__control > .v-input__slot, .v-text-field.v-text-field--enclosed .v-text-field__details {
      padding: 0px 6px;
    }
    .v-select.v-text-field--outlined:not(.v-text-field--single-line) .v-select__selections,
    .v-select.v-text-field--outlined:not(.v-text-field--single-line).v-input--dense .v-select__selections {
      padding: 0px;
    }
    .v-text-field--enclosed .v-input__append-inner {
      margin-top: 5px;
    }

    .v-text-field.text-right input {
      text-align: right;
    }

    .v-text-field.condensed > .v-input__control > .v-input__slot {
      min-height: 20px;

      input {
        max-height: 20px;
        font-size: 11px;
        padding: 0;
      }
    }

    .nutrient-label-container {
      min-width: 25px;
      max-width: 25px;
      width: 25px;
      text-align: right;
    }
  }

  .portion-container {
    .v-input input {
      max-height: 32px;
    }
    .v-text-field--filled > .v-input__control > .v-input__slot, .v-text-field--full-width > .v-input__control > .v-input__slot, .v-text-field--outlined > .v-input__control > .v-input__slot {
      min-height: 32px;
    }
    .v-text-field--outlined.v-input--dense.v-text-field--outlined > .v-input__control > .v-input__slot {
      min-height: 32px;
    }
    .v-text-field.v-text-field--enclosed:not(.v-text-field--rounded) > .v-input__control > .v-input__slot, .v-text-field.v-text-field--enclosed .v-text-field__details {
      padding: 0px 6px;
    }
    .v-select.v-text-field--outlined:not(.v-text-field--single-line) .v-select__selections,
    .v-select.v-text-field--outlined:not(.v-text-field--single-line).v-input--dense .v-select__selections {
      padding: 0px;
    }
    .v-text-field--enclosed .v-input__append-inner {
      margin-top: 5px;
    }
  }
</style>